import type {
  TeamMember, ResponseItem, Fix, CommentItem, Metrics, ColumnKey, FixStatus,
} from './types';

const BASE = ''; // vite proxy handles /api

async function json<T>(res: Response): Promise<T> {
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res.json() as Promise<T>;
}

export const api = {
  team: {
    list: () => fetch(`${BASE}/api/team`).then(json<TeamMember[]>),
    add: (name: string, role = 'member', isAdmin = false) =>
      fetch(`${BASE}/api/team`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, role, isAdmin }),
      }).then(json<TeamMember>),
    remove: (id: string) =>
      fetch(`${BASE}/api/team/${id}`, { method: 'DELETE' }).then(json<{ ok: true }>),
  },
  responses: {
    // Admin (everyone) — requires passcode.
    listWeek: (week?: string, key?: string) => {
      const qs = new URLSearchParams();
      if (week) qs.set('week', week);
      if (key) qs.set('key', key);
      const q = qs.toString();
      return fetch(`${BASE}/api/responses${q ? `?${q}` : ''}`)
        .then(json<{ weekKey: string; items: ResponseItem[] }>);
    },
    // Member — only their own, no passcode.
    listMine: (member: string, week?: string) => {
      const qs = new URLSearchParams({ member });
      if (week) qs.set('week', week);
      return fetch(`${BASE}/api/responses/mine?${qs.toString()}`)
        .then(json<{ weekKey: string; items: ResponseItem[] }>);
    },
    weeks: (key?: string) => fetch(`${BASE}/api/responses/weeks${key ? `?key=${encodeURIComponent(key)}` : ''}`).then(json<Array<{ weekKey: string; count: number }>>),
    submit: (payload: {
      memberName: string; perfect: string; wrong: string; timeSink: string; ask: string;
      weekKey?: string;
    }) =>
      fetch(`${BASE}/api/responses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }).then(json<ResponseItem>),
  },
  metrics: () => fetch(`${BASE}/api/metrics`).then(json<Metrics>),
  reactions: {
    toggle: (responseId: string, columnKey: ColumnKey, emoji: string, memberName: string) =>
      fetch(`${BASE}/api/reactions/toggle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ responseId, columnKey, emoji, memberName }),
      }).then(json<{ ok: true; removed: boolean }>),
  },
  comments: {
    list: (responseId: string) =>
      fetch(`${BASE}/api/comments/${responseId}`).then(json<CommentItem[]>),
    create: (payload: {
      responseId: string; columnKey: ColumnKey; parentId?: string | null;
      memberName: string; body: string;
    }) =>
      fetch(`${BASE}/api/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }).then(json<CommentItem>),
  },
  fixes: {
    list: (key?: string) => fetch(`${BASE}/api/fixes${key ? `?key=${encodeURIComponent(key)}` : ''}`).then(json<Fix[]>),
    create: (payload: {
      title: string; detail?: string; status?: FixStatus;
      raisedBy: string; assignedTo?: string; sourceId?: string;
    }) => fetch(`${BASE}/api/fixes`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }).then(json<Fix>),
    update: (id: string, patch: Partial<Fix>) =>
      fetch(`${BASE}/api/fixes/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      }).then(json<Fix>),
  },
  exportCsvUrl: (key?: string) => `${BASE}/api/export/csv${key ? `?key=${encodeURIComponent(key)}` : ''}`,
};
